import gsap from 'gsap';
import { createApp, onMounted } from 'vue';

import { ANIMATION_CONFIGS, ANIMATION_DURATIONS } from '~/constants/animations';

import * as AnalyticsData from '../analytics/analytics-data';
import { BaseSection } from './base-section';

const SECTION_NAME = '3x-promo-tiles';

/**
 * 3x Promo Tiles Section
 *
 * This section displays up to three promo cards.
 *
 * @extends BaseSection
 * @constructor
 * @param {HTMLElement} container - The container element for the section
 */
class Section3xPromoTiles extends BaseSection {
  constructor() {
    super(`[data-section-type="${SECTION_NAME}"]`);
  }

  init(instanceData) {
    const container = instanceData.container;
    const sectionInstance = this;

    createApp({
      name: '3xPromoTilesSection',
      setup() {
        onMounted(() => {
          // Hook analytics event to CTA links
          if (sectionInstance.isHomePageTemplate()) {
            AnalyticsData.hookHomepageModuleClicked({
              sectionName: SECTION_NAME,
            });
          }
        });

        return {};
      },
    }).mount(container);
  }

  onUnload(instanceData) {
    // Kill ScrollTrigger instances
    if (instanceData.animation && instanceData.animation.scrollTrigger) {
      instanceData.animation.scrollTrigger.kill();
    }

    // Kill the animation
    if (instanceData.animation) {
      instanceData.animation.kill();
      instanceData.animation = null;
    }

    const app = instanceData.container.__vue_app__;
    if (app) {
      app.unmount();
    }
  }

  /**
   * Sets up the animation timeline for the section that will play when it comes into view
   * This will be called automatically during initialization
   *
   * @param {Object} instanceData - The section instance data
   * @return {Object} The GSAP timeline for the animation
   */
  setupInViewAnimationTimeline(instanceData) {
    const container = instanceData.container;
    const promoCards = container.querySelectorAll('[data-content]');
    if (!container || !promoCards) return null;

    const tl = gsap.timeline({
      paused: true,
      scrollTrigger: {
        ...this.getInViewAnimationOptions(),
        trigger: container,
      },
    });

    if (!tl) return null;

    tl.fromTo(container, { opacity: 0 }, ANIMATION_CONFIGS.FADE_IN);

    tl.fromTo(
      promoCards,
      {
        opacity: 0,
      },
      ANIMATION_CONFIGS.STAGGERED_FADE_IN_SUBTLER,
      ANIMATION_DURATIONS.SECTION.FADE_IN,
    );

    return tl;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new Section3xPromoTiles();
});
